import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchServer, fetchServers, createServer, updateServer, updateServerHostedConfig, updateServerBilling,
  startServer, stopServer, restartServer, sleepServer, syncServer
} from "src/actions/mine_craft/serversActions";

// Provisioning record behind a server hosted through crafty-servers
// (Pterodactyl). Present only on hosted rows.
export interface HostedServerInterface {
  id: number;
  crafty_id: string;

  state: 'installing' | 'ready' | 'failed' | 'archived';
  status: string;
  error: string;

  // Players connected as of the last sync with crafty-servers — a
  // headline number, not the live /players read. null before the first
  // sync that carried it.
  players_online: number | null;

  config: { [key: string]: any };

  // What we asked crafty-servers to enforce at provision/resize time —
  // absent (or partial) on servers hosted before this was tracked, or
  // whose admin never set a limit. Not the same as `config`: that's
  // whatever the remote API happened to echo back, untyped.
  limits: { memory_mb?: number; disk_mb?: number; cpu_percent?: number };

  // The catalog plan this server was provisioned on — null for servers
  // created on hand-written limits, and for those whose plan was deleted.
  server_plan_id: number | null;
  server_plan_title: string | null;

  // Rent, on servers rented from the app; paid_until null = never billed.
  // Unpaid, a server runs on until grace_ends_at, is then archived (its
  // backup stored by the API, the crafty server removed) and deleted at
  // deletes_at.
  paid_until: string | null;
  rent_coins: number | null;
  rent_anchor_at: string | null;
  auto_renew: boolean;
  grace_ends_at: string | null;
  archive_requested_at: string | null;
  archived_at: string | null;
  deletes_at: string | null;
  expiry_notified_at: string | null;
  grace_notified_at: string | null;

  synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServerInterface {
  url: string;

  id: number;
  app_id: number;
  user_id: number | null;

  kind: 'user' | 'official';

  title: string;
  description: string;
  description_html: string;
  description_html_raw: string;
  search_text: string;

  image_url: string;

  ip: string;
  port: number;
  version: string;

  likes_count: number;
  comments_count: number;
  popular_count: number;

  published: boolean;

  hosted_server?: HostedServerInterface | null;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: ServerInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<ServerInterface>();

const slice = createSlice({
  name: 'servers',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      // OneSky is where official servers are minted, so the create form
      // defaults to the official kind and the Bedrock port.
      const defaults = { kind: 'official', port: 19132, published: true }

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchServers.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchServers.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.servers;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchServers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(createServer.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })

      // #update
      .addCase(updateServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(updateServer.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // hosted lifecycle — each action answers with the refreshed server
      .addCase(startServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(stopServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(restartServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(sleepServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(updateServerHostedConfig.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(updateServerBilling.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
      .addCase(syncServer.fulfilled, (state, action) => {
        const server = Object.values(action.payload.server)[0];
        state.entities[server.id] = server;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectServerById,
  selectIds: selectServerIds,
  selectEntities: selectServerEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.servers);
